/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, {
  useRef,
  useEffect,
  useCallback,
  forwardRef,
  ReactNode,
  Fragment,
  useState,
} from 'react'
import { CanvasMap, Toaster, Flex } from '@ui'
import { useReload } from '@hooks/reloadContext'
import cx from 'classnames'
import SelectionPopover from './SelectionPopover'
import FloorMapTooltip from './FloorMapTooltip'
import DragLayer from './DragLayer'
import { UseFloorHook, useMapInit, useAdminMapParams } from '@providers/mapProvider'
import { Classes } from '@blueprintjs/core'
import { useDrop, DropTargetMonitor } from 'react-dnd'
import bg from '../../assets/map-bg.png'
import { IFloorMapSpace, IFullEmployee, ISpaceAssignment, IDepartment } from '@api/types'
import api, { ICurrentUser } from '@api'
import { get, compact } from 'lodash'
import { ApolloError } from 'apollo-boost'
import { isShared, isPrimary, isFinder } from '@utils'
import { handleError, seatable, sharedDesk } from '@ui/canvas/utils'
import useMapViewSettings, { IMapViewSettings } from './hooks/useMapViewSettings'
import useAssetFilterSettings from './hooks/useAssetFilterSettings'
import MapViewSettings from './MapViewSettings'
import css from './adminFloorPlan.module.scss'
import MapActions from './MapActions/MapActions'
import addEmployeesToNeighborhood from '@api/neighborhood/addEmployees'
import ConfirmDialog from './Cards/ConfirmDialog'
import { UnassignEmployeeDropArea } from './Cards/UnassignEmployeeDragArea'

interface MapProps {
  splitScreen?: boolean
  showViewFilters?: boolean
  setShowViewFilters?: (showViewFilters: boolean) => void
  showMapActions?: boolean
  floor: UseFloorHook
  user: ICurrentUser | null
  wrapperClassName?: string
  bottomMapContainerElement?: ReactNode
  pinLeftNav?: boolean
  mapViewSetting?: ({
    mapViewSettings,
    updateMapViewSettings,
  }: {
    mapViewSettings: IMapViewSettings
    updateMapViewSettings: any
  }) => void
  assetFilterSetting?: ({
    assetFilterSettings,
    updateAssetFilterSettings,
  }: {
    assetFilterSettings: string[]
    updateAssetFilterSettings: any
  }) => void
}

const Map = forwardRef<HTMLDivElement, MapProps>(
  (
    {
      floor,
      mapViewSetting,
      assetFilterSetting,
      user,
      splitScreen,
      showViewFilters,
      setShowViewFilters,
      showMapActions = true,
      wrapperClassName,
      bottomMapContainerElement,
      pinLeftNav,
    },
    ref
  ) => {
    const reload = useReload()
    const containerRef = useRef<HTMLDivElement>(null)
    const mapRef = useMapInit()
    const params = useAdminMapParams()

    const { mapViewSettings, updateMapViewSettings } = useMapViewSettings()
    const { assetFilterSettings, updateAssetFilterSettings } = useAssetFilterSettings()

    const [dropProps, dropRef] = useDrop({
      accept: 'Employee',
      collect: (monitor: DropTargetMonitor) => ({
        item: monitor.getItem(),
      }),
    })
    // Find space where employee maybe seated and unseat
    const findEmployeeSpace = useCallback(
      (employeeId: string) => {
        return floor.spaces.ids.reduce((result: IFloorMapSpace | null, id: string) => {
          if (result) return result
          const space = floor.spaces.byId[id]
          if (!seatable(space.spaceType)) return null
          const shared = isShared(space.usageType)
          const primary = isPrimary(space.usageType)

          if (shared) {
            const sharedEmployees = get(space, 'spaceAssignment.sharedEmployees') || []
            if (sharedEmployees.find(({ id }: IFullEmployee) => id === employeeId)) {
              return {
                ...space,
                spaceAssignment: {
                  ...(space.spaceAssignment as ISpaceAssignment),
                  sharedEmployees: sharedEmployees.filter(
                    ({ id }: IFullEmployee) => id !== employeeId
                  ),
                },
              }
            }
          } else if (primary && get(space, 'spaceAssignment.employee.id') === employeeId) {
            return {
              ...space,
              spaceAssignment: {
                ...(space.spaceAssignment as ISpaceAssignment),
                employee: null,
                department: null,
                team: null,
              },
            }
          }

          return null
        }, null)
      },
      [floor]
    )

    const seatEmployee = useCallback(
      (employee: IFullEmployee, toSpace: IFloorMapSpace) => {
        const shared = isShared(toSpace.usageType)
        let nextDepartment = get(employee, 'dept')
        let nextTeam = get(employee, 'team')
        let nextSharedEmployees = null

        if (shared) {
          const sharedEmployees = get(toSpace, 'spaceAssignment.sharedEmployees') || []
          if (!!sharedEmployees.find(({ id }: IFullEmployee) => id === employee.id)) {
            nextSharedEmployees = sharedEmployees.filter(
              ({ id }: IFullEmployee) => id !== employee.id
            )
            nextDepartment = get(toSpace, 'spaceAssignment.department', {}) as IDepartment
            nextTeam = get(toSpace, 'spaceAssignment.team', {}) as IDepartment
          } else {
            nextSharedEmployees = [...sharedEmployees, employee]
            nextDepartment = employee.dept
            nextTeam = employee.team
          }
        }

        const nextToSpace = {
          ...toSpace,
          spaceAssignment: {
            ...(toSpace.spaceAssignment as ISpaceAssignment),
            employee: shared ? null : employee,
            sharedEmployees: nextSharedEmployees,
            department: nextDepartment,
            team: nextTeam,
          },
        }

        const nextFromSpace = findEmployeeSpace(employee.id)
        const assignmentPlanId = floor.apId
        const oldSpace = toSpace

        const space = {
          id: toSpace.id,
          externalId: oldSpace.externalId,
          spaceAssignment: {
            department: employee.dept || employee.department,
            team: employee.team,
            employee: sharedDesk(oldSpace.usageType)
              ? null
              : { ...employee, tags: [], departmentAdmins: [] },
            sharedEmployees: sharedDesk(oldSpace.usageType)
              ? oldSpace.spaceAssignment.sharedEmployees
                ? [...oldSpace.spaceAssignment.sharedEmployees, employee]
                : [employee]
              : [],
          },
          sourceAssignmentPlanId: assignmentPlanId,
        }

        if (
          employee.isRemote ||
          employee.noDeskNeeded ||
          (employee.neighborhoods && employee.neighborhoods.length)
        ) {
          return floor.setConfirm?.({ employee, space, nextFromSpace, nextToSpace })
        }
        floor.updateSpaces?.(compact([nextFromSpace, nextToSpace]))

        api.space
          .seat({
            assignmentPlanId: floor.apId,
            toSpaceId: toSpace.id,
            employeeId: employee.id,
            moveType: 'move',
          })
          .then(() => {
            reload.employeeCatalog()
            Toaster.success({
              message: `Moved ${employee.name} to a ${toSpace.code}`,
            })
          })
          .catch((error: ApolloError) => {
            Toaster.fail({ error })
          })
      },
      [floor, reload, findEmployeeSpace]
    )

    const unseatEmployee = (employee: IFullEmployee) => {
      const employeeSpace = findEmployeeSpace(employee?.id)
      employeeSpace && floor.updateSpaces?.([employeeSpace])
      api.space
        .seat({
          assignmentPlanId: floor.apId,
          toSpaceId: null,
          fromSpaceId: employeeSpace?.id,
          employeeId: employee?.id,
          moveType: 'move',
        })
        .then(() => {
          Toaster.success({
            message: 'Employee was unseated',
          })
        })
        .catch(handleError)
    }

    const dragEmployeeToNeighborhood = useCallback(
      (nh: any, toSpace: IFloorMapSpace, space: IFloorMapSpace) => {
        const employeeId = get(space, 'spaceAssignment.employee.id', '')
        const employeeName = get(space, 'spaceAssignment.employee.name', '')

        addEmployeesToNeighborhood(
          get(nh, 'id') || get(toSpace, 'spaceAssignment.neighborhood.id'),
          [employeeId]
        )
          .then((data: any) => {
            space.spaceAssignment.employee = null
            floor.updateSpaces?.([space])
            Toaster.success({
              message: ` ${employeeName} were associated to ${data.name}`,
            })
          })
          .catch(handleError)
      },
      [floor]
    )

    const dragEmployeeToNeighborhoodFromSearch = useCallback(
      (nh: any, toSpace: IFloorMapSpace, employee: IFullEmployee) => {
        const employeeId = employee.id
        const employeeName = employee.name
        const isSpace = findEmployeeSpace(employee.id)
        addEmployeesToNeighborhood(
          get(nh, 'id') || get(toSpace, 'spaceAssignment.neighborhood.id'),
          [employeeId]
        )
          .then((data: any) => {
            isSpace && floor.updateSpaces?.([isSpace])
            Toaster.success({
              message: ` ${employeeName} were associated to ${data.name}`,
            })
          })
          .catch(handleError)
      },
      [floor, findEmployeeSpace]
    )

    const youAreHere = () => {
      let youAreHere = null
      if (
        isFinder(user?.role) &&
        !floor.isBookDeskPage &&
        !floor.isBookMeetingRoomPage &&
        floor.floorId === get(user, 'space.floor.id')
      ) {
        youAreHere = user?.space
      }
      return youAreHere
    }

    useEffect(() => {
      if (containerRef.current && floor.map && user) {
        const rect = containerRef.current.getBoundingClientRect()

        if (mapRef.current) {
          // @ts-ignore
          mapRef.current.update({
            screen: { w: rect.width - 50, h: rect.height },
            map: {
              url: floor.map.assetVersion?.background?.url,
              w: floor.map.assetVersion.mapW,
              h: floor.map.assetVersion.mapH,
            },
            pixelsPerFoot: floor.map.scale,
            assetVersion: floor.map.assetVersion,
            blockInstances: { ids: [], byId: [] },
            spaces: floor.spaces,
            pins: floor.pins,
            devices: { ids: [], byId: [] },
            filteredIds: {},
            selectedIds: floor.selection?.items,
            selectedNeighborhoodId: params.selectedNeighborhoodId,
            selectedDepartmentId: params.selectedDepartmentId,
            showDepartmentsArea: mapViewSettings.aDepartment === 'On',
            showFacilityGroupsArea: false,
            showNeighborhoodsArea: mapViewSettings.aNeighborhood === 'On',
            showTeamsArea: mapViewSettings.aTeam === 'On',
            showWorkplaceGroupsEmployeesArea: mapViewSettings.aEWorkplaceGroup === 'On',
            showWorkplaceGroupsSpacesArea: mapViewSettings.aWorkplaceGroup === 'On',
            floor,
            force: { isCirclesEnabled: true },
            liveModeOn: false,
            mapViewSettings,
            assetFilterSettings,
            seatEmployee,
            unseatEmployee,
            dragEmployeeToNeighborhood,
            addEmployeesToNeighborhood,
            dragEmployeeToNeighborhoodFromSearch,
            findEmployeeSpace,
            updateYouAreHere: !!isFinder(user.role),
            youAreHere: youAreHere(),
            isDirectBookingFlow: floor.isBookDeskPage || floor.isBookMeetingRoomPage,
          })
        } else {
          // @ts-ignore
          mapRef.current = new CanvasMap({
            enableTooltip: true,
            enableSelection: true,
            enableMultiSelection: true,
            enableZoomWidget: true,
            enableEmployeeDrag: true,
            container: containerRef.current,
            screen: { w: rect.width, h: rect.height },
            map: {
              url: floor.map.assetVersion?.background?.url,
              w: floor.map.assetVersion.mapW,
              h: floor.map.assetVersion.mapH,
            },
            pixelsPerFoot: floor.map.scale,
            assetVersion: floor.map.assetVersion,
            blockInstances: { ids: [], byId: [] },
            spaces: floor.spaces,
            pins: floor.pins,
            devices: { ids: [], byId: [] },
            filteredIds: {},
            selectedIds: floor.selection?.items,
            selectedNeighborhoodId: params.selectedNeighborhoodId,
            selectedDepartmentId: params.selectedDepartmentId,
            showDepartmentsArea: mapViewSettings.aDepartment === 'On',
            showFacilityGroupsArea: false,
            showNeighborhoodsArea: mapViewSettings.aNeighborhood === 'On',
            showTeamsArea: mapViewSettings.aTeam === 'On',
            showWorkplaceGroupsEmployeesArea: mapViewSettings.aEWorkplaceGroup === 'On',
            showWorkplaceGroupsSpacesArea: mapViewSettings.aWorkplaceGroup === 'On',
            force: { isCirclesEnabled: true },
            liveModeOn: false,
            toolbarOffset: 0,
            user: user.employee,
            onZoom: () => null,
            onZoomEnd: () => null,
            floor,
            select: floor.selection?.toggle,
            multiSelect: floor.selection?.add,
            deselect: floor.selection?.remove,
            deselectAll: floor.selection?.clear,
            seatEmployee,
            unseatEmployee,
            mapViewSettings,
            assetFilterSettings,
            dragEmployeeToNeighborhood,
            addEmployeesToNeighborhood,
            dragEmployeeToNeighborhoodFromSearch,
            findEmployeeSpace,
            youAreHere: youAreHere(),
            isDirectBookingFlow: floor.isBookDeskPage || floor.isBookMeetingRoomPage,
          })
          // @ts-ignore
          mapRef.current.draw()
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      floor,
      user,
      seatEmployee,
      unseatEmployee,
      mapViewSettings,
      assetFilterSettings,
      params.selectedNeighborhoodId,
      params.selectedDepartmentId,
    ])

    useEffect(() => {
      return () => {
        // @ts-ignore
        mapRef.current && mapRef.current.destroy()
        // @ts-ignore
        mapRef.current = null
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
      // @ts-ignore
      areaId: mapRef?.current?.selectedNeighborhoodId &&
        // @ts-ignore
        mapRef?.current?.zoomToArea({ areaId: mapRef?.current?.selectedNeighborhoodId })
      // @ts-ignore
    }, [mapRef?.current?.areas])

    useEffect(() => {
      mapViewSetting?.({
        mapViewSettings: mapViewSettings,
        updateMapViewSettings: updateMapViewSettings,
      })
    }, [mapViewSettings])

    useEffect(() => {
      assetFilterSetting?.({
        assetFilterSettings: assetFilterSettings,
        updateAssetFilterSettings: updateAssetFilterSettings,
      })
    }, [assetFilterSettings])

    const renderMap = () => (
      <div
        className={css.map_container}
        style={{
          backgroundImage: `url(${bg})`,
        }}
        ref={containerRef}>
        {!floor.floorId && (
          <Flex align="center" justify="center" style={{ height: '60%' }}>
            Select a floor
          </Flex>
        )}
      </div>
    )

    return (
      <Fragment>
        <ConfirmDialog floor={floor} />
        <div
          className={css.container}
          ref={dropRef}
          style={{
            right: splitScreen ? 360 : 0,
            left: pinLeftNav ? 270 : 70,
          }}
          onDragOver={({ clientX: x, clientY: y }) => {
            x -= pinLeftNav ? 270 : 75
            y -= 75
            // @ts-ignore
            mapRef.current && mapRef.current.handleOutsideDrag({ x, y }, dropProps.item.data)
          }}
          onDrop={({ clientX: x, clientY: y }) => {
            x -= 200
            // @ts-ignore
            mapRef.current && mapRef.current.handleOutsideDrop({ x, y }, dropProps.item.data)
          }}>
          {!wrapperClassName || !ref ? (
            renderMap()
          ) : (
            <div className={wrapperClassName} ref={ref}>
              {renderMap()}
            </div>
          )}
          {/* {showMapActions && floor.map && (
            <div className={cx(css.floating_button_container, Classes.POPOVER)}>
              <MapViewSettings {...{ mapViewSettings, updateMapViewSettings }} />
            </div>
          )} */}
          {mapRef.current && (
            <SelectionPopover
              key={Object.keys(floor.selection?.items || []).join('')}
              floor={floor}
              canvasMap={mapRef.current}
            />
          )}
          {mapRef.current && <FloorMapTooltip canvasMap={mapRef.current} />}
          <DragLayer />
          {mapRef.current && <UnassignEmployeeDropArea mapRef={mapRef.current} />}
          {showMapActions && (
            <MapActions
              setShowViewFilters={setShowViewFilters}
              showViewFilters={showViewFilters}
              canvasMap={mapRef.current}
            />
          )}
          {!!bottomMapContainerElement && bottomMapContainerElement}
        </div>
      </Fragment>
    )
  }
)

export default Map
